import React, { useState, useEffect } from 'react';
import API_BASE_URL from '../config';
import { 
  LifeBuoy, 
  Search, 
  Filter, 
  CheckCircle2, 
  RefreshCw, 
  AlertCircle 
} from 'lucide-react';

const Reports = () => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [gymFilter, setGymFilter] = useState('All');
  const [actionLoading, setActionLoading] = useState(null); // stores reportId of current patch

  const fetchReports = async () => {
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_BASE_URL}/api/UserReport`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to retrieve support reports.');
      }

      const result = await response.json();
      if (result.success) {
        setReports(result.data);
      }
    } catch (err) {
      console.error(err);
      setError('Could not connect to API. Displaying simulated report records.');
      setReports(getMockReports());
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleResolve = async (reportId) => {
    setActionLoading(reportId);
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_BASE_URL}/api/UserReport/${reportId}/status`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: 'Resolved' })
      });

      if (!response.ok) {
        throw new Error('Failed to update report status.');
      }

      // Locally update
      setReports(prev => prev.map(r => r.id === reportId ? { ...r, status: 'Resolved' } : r));
    } catch (err) {
      console.error(err);
      alert('Failed to resolve report. Updating simulated local state instead.');
      setReports(prev => prev.map(r => r.id === reportId ? { ...r, status: 'Resolved' } : r));
    } finally {
      setActionLoading(null);
    }
  };

  const getMockReports = () => {
    return [
      { id: 1, username: "john.doe@gmail.com", displayName: "John Doe", gymcode: "MUS-1029", gymName: "Muscle Factory", description: "My QR code fails to scan. The screen brightness is full, but the terminal camera does not blink. Please check.", createdAt: new Date(Date.now() - 3600000 * 2).toISOString(), status: "Pending" },
      { id: 2, username: "priya123", displayName: "Priya Sharma", gymcode: "IRO-8839", gymName: "Iron House", description: "The daily reward ad failed to load on my Score tab. It shows a blank grey screen when clicked.", createdAt: new Date(Date.now() - 3600000 * 24).toISOString(), status: "Resolved" },
      { id: 3, username: "sanjay_r", displayName: "Sanjay Reddy", gymcode: "MUS-1029", gymName: "Muscle Factory", description: "My consistency score did not update today after my check-in. It still shows yesterday's streak of 12.", createdAt: new Date(Date.now() - 3600000 * 48).toISOString(), status: "Pending" }
    ];
  };

  // Get unique gym names for filter dropdown
  const getUniqueGyms = () => {
    const gyms = reports.map(r => r.gymName).filter(Boolean);
    return ['All', ...new Set(gyms)];
  };

  // Filtered reports
  const filteredReports = reports.filter(r => {
    const matchesSearch = 
      r.displayName.toLowerCase().includes(search.toLowerCase()) || 
      r.username.toLowerCase().includes(search.toLowerCase()) || 
      r.description.toLowerCase().includes(search.toLowerCase());

    const matchesStatus = statusFilter === 'All' ? true : r.status === statusFilter;
    const matchesGym = gymFilter === 'All' ? true : r.gymName === gymFilter;

    return matchesSearch && matchesStatus && matchesGym;
  });

  return (
    <div className="space-y-6 animate-fadeIn">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white tracking-tight flex items-center gap-2.5">
            <LifeBuoy className="text-[#6CD800]" size={28} />
            User Support Reports
          </h1>
          <p className="text-gray-400 text-sm mt-1">Review, filter, and resolve issues reported by gym members.</p>
        </div>
        <button 
          onClick={fetchReports}
          className="flex items-center px-4 py-2 bg-[#1E293B] border border-[#334155] hover:border-[#6CD800] rounded-lg text-sm text-gray-300 transition-all hover:text-white w-fit"
        >
          <RefreshCw size={16} className="mr-2" />
          Refresh
        </button>
      </div>

      {error && (
        <div className="bg-yellow-500/10 border border-yellow-500/30 text-yellow-400 text-sm rounded-lg p-4 flex items-center gap-2">
          <AlertCircle size={18} />
          {error}
        </div>
      )}

      {/* Filter and Search Panel */}
      <div className="bg-[#0F172A] border border-[#1E293B] rounded-xl p-5 shadow-lg flex flex-col md:flex-row gap-4 justify-between items-center">
        <div className="relative w-full md:max-w-sm">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search reports by user or description..."
            className="w-full pl-10 pr-4 py-2 bg-[#0B0F19] border border-[#1E293B] rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#6CD800] text-sm transition-colors"
          />
          <span className="absolute left-3 top-2.5 text-gray-500">
            <Search size={18} />
          </span>
        </div>

        <div className="flex flex-wrap gap-4 w-full md:w-auto justify-end">
          <div className="flex items-center space-x-2 bg-[#0B0F19] border border-[#1E293B] px-3 py-2 rounded-lg">
            <Filter size={14} className="text-gray-400" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="bg-transparent border-none text-xs text-gray-300 focus:outline-none cursor-pointer"
            >
              <option value="All">All Statuses</option>
              <option value="Pending">Pending</option>
              <option value="Resolved">Resolved</option>
            </select>
          </div>

          <div className="flex items-center space-x-2 bg-[#0B0F19] border border-[#1E293B] px-3 py-2 rounded-lg">
            <Filter size={14} className="text-gray-400" />
            <select
              value={gymFilter}
              onChange={(e) => setGymFilter(e.target.value)}
              className="bg-transparent border-none text-xs text-gray-300 focus:outline-none cursor-pointer"
            >
              {getUniqueGyms().map(g => (
                <option key={g} value={g}>{g === 'All' ? 'All Gyms' : g}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Main Table Grid */}
      <div className="bg-[#0F172A] border border-[#1E293B] rounded-xl shadow-lg overflow-hidden">
        {loading ? (
          <div className="flex justify-center items-center h-48 text-[#6CD800]">
            <RefreshCw size={24} className="animate-spin mr-2" />
            Loading support reports...
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm text-gray-300">
              <thead className="text-xs text-gray-400 uppercase bg-[#0B0F19] border-b border-[#1E293B]">
                <tr>
                  <th scope="col" className="px-6 py-4">User</th>
                  <th scope="col" className="px-6 py-4">Gym</th>
                  <th scope="col" className="px-6 py-4 w-96">Description</th>
                  <th scope="col" className="px-6 py-4">Date Reported</th>
                  <th scope="col" className="px-6 py-4">Status</th>
                  <th scope="col" className="px-6 py-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#1E293B]">
                {filteredReports.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="text-center py-12 text-gray-500">No support reports found.</td>
                  </tr>
                ) : (
                  filteredReports.map((report) => (
                    <tr key={report.id} className="hover:bg-[#1E293B]/20 transition-colors">
                      <td className="px-6 py-4">
                        <span className="block font-semibold text-white">{report.displayName}</span>
                        <span className="block text-xs text-gray-400">{report.username}</span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="block text-white font-medium">{report.gymName}</span>
                        <span className="text-[#6CD800] text-xs font-mono font-bold tracking-wider">{report.gymcode || 'N/A'}</span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-300 leading-relaxed whitespace-pre-wrap">
                        {report.description}
                      </td>
                      <td className="px-6 py-4 text-xs text-gray-500">
                        {new Date(report.createdAt).toLocaleString()}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border ${
                          report.status === 'Resolved' 
                            ? 'bg-green-500/10 text-green-400 border-green-500/20' 
                            : 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20'
                        }`}>
                          <span className={`h-1.5 w-1.5 rounded-full mr-1.5 ${
                            report.status === 'Resolved' ? 'bg-green-400' : 'bg-yellow-400'
                          }`}></span>
                          {report.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right">
                        {report.status === 'Pending' ? (
                          <button
                            onClick={() => handleResolve(report.id)}
                            disabled={actionLoading === report.id}
                            className="inline-flex items-center gap-1 px-3 py-1.5 bg-[#6CD800] hover:bg-[#5bbf00] text-[#0B0F19] font-bold rounded-lg text-xs transition-colors disabled:opacity-50"
                          >
                            {actionLoading === report.id ? (
                              <RefreshCw size={12} className="animate-spin" />
                            ) : (
                              <CheckCircle2 size={12} />
                            )}
                            Resolve
                          </button>
                        ) : (
                          <span className="text-xs text-gray-500 font-medium flex items-center justify-end gap-1">
                            <CheckCircle2 size={12} className="text-green-500" />
                            Completed
                          </span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default Reports;
